import {
  PRODUCT_SEARCH_INDEXES,
  PRODUCT_SORT_OPTIONS,
  SEARCH_INDEXES,
} from '@bulknutrients/bulk-shared-lib/constants';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { Container } from '@components/Container';
import { Input, Select } from '@components/FormComponents';
import { StandardLayout } from '@components/Layouts';
import { LoadMore } from '@components/LoadMore/LoadMore';
import { SearchResults } from '@components/Search/SearchResults';
import { useSearch } from '@hooks/useSearch';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { type ReactElement, useEffect, useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

export const RESULT_TYPES = {
  PRODUCTS: {
    label: 'Products',
    index: SEARCH_INDEXES.PRODUCTS,
  },
  BLOGS: {
    label: 'Blogs',
    index: SEARCH_INDEXES.BLOGS,
  },
  EXPERTS: {
    label: 'Experts',
    index: SEARCH_INDEXES.AUTHORS,
  },
  INFORMATION_PAGES: {
    label: 'Information Pages',
    index: SEARCH_INDEXES.PAGES,
  },
  AMBASSADORS: {
    label: 'Ambassadors',
    index: SEARCH_INDEXES.AMBASSADORS,
  },
  CATEGORIES: {
    label: 'Categories',
    index: SEARCH_INDEXES.CATEGORIES,
  },
  FAQS: {
    label: 'FAQs',
    index: SEARCH_INDEXES.FAQS,
  },
};

function ResultsBlock({ query }) {
  const { setValue, getValues, watch } = useFormContext();
  const selectedIndex = getValues('index');
  const sortOption = watch('sortOption');
  // Insid e your component
  const searchParams = useMemo(
    () => ({
      index: sortOption ? SEARCH_INDEXES[sortOption] : selectedIndex,
      query,
    }),
    [sortOption, selectedIndex, query]
  );

  const { isLoadingMore, isReachingEnd, data, counts, size, setSize } =
    useSearch(searchParams);

  if (counts?.find((d) => d.index === selectedIndex)?.hits === 0) {
    const newIndex = counts.find((d) => d.hits > 0)?.index;
    if (newIndex) setValue('index', newIndex);
  }

  // On load and query change, send an analytics event to record query and number of results
  // Debounced by 3 seconds because that seems reasonable
  useEffect(() => {
    // @ts-expect-error
    const umami = window.umami;
    if (umami) {
      const trackingEvent = setTimeout(() => {
        umami.track('search-results', {
          query: query,
          numberOfResults: counts?.find((d) => d.index === selectedIndex)?.hits,
          index: selectedIndex,
        });
      }, 3000);

      return () => {
        clearTimeout(trackingEvent);
      };
    }
  }, [counts, query, selectedIndex]);

  return (
    <>
      <div className="mb-4 mt-6 flex flex-wrap gap-2 sm:gap-4 sm:no-wrap">
        <Select
          name="index"
          labelClasses="!p-0"
          label="Searching"
          className="w-fit min-w-[200px]"
          options={Object.keys(RESULT_TYPES).map((resultType) => {
            const count =
              counts?.find((d) => d.index === RESULT_TYPES[resultType].index)
                ?.hits ??
              counts?.find((d) => PRODUCT_SEARCH_INDEXES.includes(d.index))
                ?.hits;

            return {
              value: RESULT_TYPES[resultType].index,
              label: `${RESULT_TYPES[resultType].label} ${count ? `(${count})` : ''}`,
              disabled: count === 0,
            };
          })}
        />

        {PRODUCT_SEARCH_INDEXES.includes(selectedIndex) && (
          <Select
            name="sortOption"
            label="Sort by"
            labelClasses="!p-0"
            className="w-fit min-w-[200px]"
            options={Object.entries(PRODUCT_SORT_OPTIONS).map(
              ([key, value]) => ({
                value: key,
                label: value,
              })
            )}
          />
        )}
      </div>
      {/*add fixed height to prevent layout shift when products finally load*/}
      <div className={isLoadingMore ? 'min-h-[500px]' : 'flex flex-col gap-4'}>
        <SearchResults
          index={sortOption ? SEARCH_INDEXES[sortOption] : selectedIndex}
          data={data ?? []}
        />
        <LoadMore
          clickHandler={() => setSize(size + 1)}
          disabled={(isLoadingMore || isReachingEnd) ?? false}
          text={
            isLoadingMore
              ? 'loading...'
              : isReachingEnd
                ? 'no more results'
                : 'load more'
          }
          viewedResults={
            data?.find(
              (d) =>
                d.index ===
                (sortOption ? SEARCH_INDEXES[sortOption] : selectedIndex)
            )?.hits.length ?? 0
          }
          totalResults={
            counts?.find(
              (d) =>
                d.index ===
                (sortOption ? SEARCH_INDEXES[sortOption] : selectedIndex)
            )?.hits
          }
        />
      </div>
    </>
  );
}

export default function SearchResultsPage(): ReactElement {
  const router = useRouter();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
    defaultValues: {
      index: SEARCH_INDEXES.PRODUCTS,
    },
  });

  const query = rhfMethods.watch('query');
  const formIndex = rhfMethods.watch('index');

  // Handle passed query params
  useEffect(() => {
    const { q, index } = router.query;

    if (q && q !== 'undefined') {
      rhfMethods.setValue('query', q as string);
    }

    if (index && Object.values(SEARCH_INDEXES).includes(index as any)) {
      rhfMethods.setValue('index', index as string);
    } else {
      rhfMethods.setValue('index', RESULT_TYPES.PRODUCTS.index);
      rhfMethods.setValue('sortOption', 'PRODUCTS');
    }
  }, [rhfMethods, router.query]);

  useEffect(() => {
    // when user clicks back to products after viewing other indices (ambassadors, categories, etc),
    // we want sort option to default back to relevant
    if (formIndex === SEARCH_INDEXES.PRODUCTS)
      rhfMethods.setValue('sortOption', 'PRODUCTS');
    else rhfMethods.resetField('sortOption');
  }, [formIndex, rhfMethods]);

  const onSubmit = (data, e) => {
    e.preventDefault();
  };

  return (
    <>
      <Head>
        <title>Search Bulk Nutrients for Products, Blogs and more</title>
        <meta
          name="title"
          content="Search Bulk Nutrients for Products, Blogs and more"
        />
        <meta
          name="description"
          content="Search online supplement store Bulk Nutrients for Products, Blogs, Ambassadors, Information Pages, Categories and more. Search supplements and products"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/search-results-page`}
        />
      </Head>
      <Container as="main">
        <h1>Search Bulk Nutrients</h1>
        <FormProvider {...rhfMethods}>
          <form onSubmit={rhfMethods.handleSubmit(onSubmit)}>
            <Input type="search" name="query" label="" className="w-full" />
          </form>
          <BulkErrorBoundary location="Search results">
            <ResultsBlock query={query ?? ''} />
          </BulkErrorBoundary>
        </FormProvider>
      </Container>
    </>
  );
}

SearchResultsPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
